import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Custom hook for managing iOS/Android app icon badge
 * 
 * Uses @capawesome/capacitor-badge plugin for native badge support.
 * Falls back gracefully on web where badges are not supported.
 */

// Dynamically import the badge plugin to avoid errors on web
let Badge: typeof import('@capawesome/capacitor-badge').Badge | null = null;

// Initialize badge plugin only on native platforms
const initBadge = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const module = await import('@capawesome/capacitor-badge');
      Badge = module.Badge;
    } catch (error) {
      console.warn('Badge plugin not available:', error);
    }
  }
};

// Initialize on module load
initBadge();

interface UseBadgeReturn {
  isSupported: boolean;
  setBadge: (count: number) => Promise<void>;
  clearBadge: () => Promise<void>;
  getBadge: () => Promise<number>;
  requestPermission: () => Promise<boolean>;
}

export const useBadge = (): UseBadgeReturn => {
  const [isSupported, setIsSupported] = useState(false);

  // Check if badge is supported
  useEffect(() => {
    let isMounted = true;

    const checkSupport = async () => {
      if (!Badge) {
        if (isMounted) {
          setIsSupported(false);
        }
        return;
      }

      try {
        const result = await Badge.isSupported();
        if (isMounted) {
          setIsSupported(result.isSupported);
        }
      } catch (error) {
        console.warn('Error checking badge support:', error);
        if (isMounted) {
          setIsSupported(false);
        }
      }
    };

    // Wait a bit for the plugin to initialize
    const timeout = setTimeout(checkSupport, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, []);

  // Set badge count
  const setBadge = useCallback(async (count: number) => {
    if (!Badge) return;

    try {
      await Badge.set({ count });
    } catch (error) {
      console.warn('Error setting badge:', error);
    }
  }, []);

  // Clear badge
  const clearBadge = useCallback(async () => {
    if (!Badge) return;

    try {
      await Badge.clear();
    } catch (error) {
      console.warn('Error clearing badge:', error);
    }
  }, []);

  // Get current badge count
  const getBadge = useCallback(async (): Promise<number> => {
    if (!Badge) return 0;

    try {
      const result = await Badge.get();
      return result.count;
    } catch (error) {
      console.warn('Error getting badge:', error);
      return 0;
    }
  }, []);

  // Request permission to display badge
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!Badge) return false;

    try {
      const result = await Badge.requestPermissions();
      return result.display === 'granted';
    } catch (error) {
      console.warn('Error requesting badge permission:', error);
      return false;
    }
  }, []);

  return {
    isSupported,
    setBadge,
    clearBadge,
    getBadge,
    requestPermission,
  };
};

export default useBadge;

